import { useMemo } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { type Column } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { ELIGIBILITY_RULES_DESCRIPTION, RESTRICTIVE_ELIGIBILITY_RULES } from 'constants/eligibilityRules';
import { EligibilityStatus, type PersonEventEligibility } from 'typedefs/event';

const { ELIGIBLE, INELIGIBLE } = EligibilityStatus;

const columnHelper = createColumnHelper<PersonEventEligibility>();

export const getSortedParticipantsEligibility = (
  participantsEventEligibility: PersonEventEligibility[],
  isUnsanctionedTournament: boolean,
): {
  eligibleParticipants: PersonEventEligibility[];
  partEligibleParticipants: PersonEventEligibility[];
  ineligibleParticipants: PersonEventEligibility[];
} => {
  const ineligibileGroup = !isUnsanctionedTournament
    ? participantsEventEligibility?.filter((p) => p?.eligibilityStatus === INELIGIBLE)
    : [];

  const eligibleParticipants = participantsEventEligibility?.filter(
    (p) => p?.eligibilityStatus === ELIGIBLE || (isUnsanctionedTournament && p),
  );

  const [ineligibleParticipants, partEligibleParticipants] = ineligibileGroup.reduce(
    (acc, curr) => {
      const { eligibilityRuleStatuses } = curr;
      const isFullyIneligible = eligibilityRuleStatuses?.some(
        (rule) => rule?.status === INELIGIBLE && RESTRICTIVE_ELIGIBILITY_RULES.includes(rule?.ruleId),
      );

      const filteredRuleStatuses = eligibilityRuleStatuses?.filter(
        (rule) =>
          rule?.status === INELIGIBLE &&
          ((isFullyIneligible && RESTRICTIVE_ELIGIBILITY_RULES.includes(rule?.ruleId)) || !isFullyIneligible),
      );

      return isFullyIneligible
        ? [[...acc[0], { ...curr, eligibilityRuleStatuses: filteredRuleStatuses }], acc[1]]
        : [acc[0], [...acc[1], { ...curr, eligibilityRuleStatuses: filteredRuleStatuses }]];
    },
    [[], []],
  );

  return {
    eligibleParticipants,
    partEligibleParticipants,
    ineligibleParticipants,
  };
};

export const getParticipantDisplayName = (participant, isTeam) =>
  isTeam
    ? participant?.participantName
    : `${participant?.person?.standardFamilyName}, ${participant?.person?.standardGivenName}`;

export const useEligibilityTableColumns = (): Column<PersonEventEligibility>[] => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        columnHelper.accessor(({ participantName }) => participantName, {
          id: 'player',
          header: t('ply'),
          cell: ({ getValue }) => getValue(),
        }),
        columnHelper.accessor(
          ({ eligibilityRuleStatuses }) =>
            eligibilityRuleStatuses
              ?.map((e) => ELIGIBILITY_RULES_DESCRIPTION?.find((rule) => rule?.ruleId === e?.ruleId)?.short)
              ?.join(', '),
          { id: 'eligibilityIssues', header: t('eligibilityIssues'), cell: ({ getValue }) => getValue() },
        ),
      ] as Column<PersonEventEligibility>[],
    [t],
  );
};
